import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  AlertTriangle,
  ArrowLeft,
  ArrowRight,
  BookOpen,
  Check,
  CheckCircle2,
  Copy,
  Loader2,
  Play,
  Plus,
  RefreshCw,
  Send,
  XCircle,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { BrandGlyph } from '@/components/brand';
import { CopyId, PageHeader, StatusPill } from '@/components/page';
import {
  API_URL,
  Callout,
  CodeBlock,
  DevLabel,
  EmptyState,
  Hint,
  InlineError,
  Mono,
  SCOPE_INFO,
  ScopePicker,
  Tag,
} from '@/components/oauth';
import { useAuth, useClients, useToast } from '@/hooks';
import {
  PG_CALLBACK_KEY,
  PG_SESSION_KEY,
  clearSession,
  decodeJwt,
  randomToken,
  readSession,
  s256,
  writeSession,
  type PlaygroundCallback,
  type PlaygroundSession,
} from '@/lib/playground';
import { cn } from '@/lib/utils';

const STEPS = ['Configure', 'Sign in', 'Callback', 'Tokens'] as const;
const CALLBACK_URI = () => `${window.location.origin}/playground/callback`;

interface TokenResult {
  ok: boolean;
  status: number;
  body: Record<string, unknown>;
}

const shorten = (t: string, n = 28) => (t.length > n ? `${t.slice(0, n)}…` : t);

export function PlaygroundPage() {
  const { user } = useAuth();
  const { toast } = useToast();
  const [params] = useSearchParams();
  const { data: allClients = [], isLoading } = useClients();
  const canCreate = user?.is_super_admin === true;

  const clients = useMemo(
    () =>
      allClients.filter(
        (c) =>
          c.client_type === 'public' &&
          c.is_active &&
          c.allowed_grant_types.includes('authorization_code') &&
          c.redirect_uris.length > 0
      ),
    [allClients]
  );

  // Resume after the redirect round trip.
  const saved = useRef(readSession<PlaygroundSession>(PG_SESSION_KEY));
  const [callback, setCallback] = useState<PlaygroundCallback | null>(() =>
    readSession<PlaygroundCallback>(PG_CALLBACK_KEY)
  );
  const resumed = !!(saved.current && callback);

  const [step, setStep] = useState(resumed ? 3 : 1);
  const [reached, setReached] = useState(resumed ? 3 : 1);

  const [clientRowId, setClientRowId] = useState(saved.current?.clientRowId || params.get('client') || '');
  const [redirect, setRedirect] = useState(saved.current?.redirectUri || '');
  const [scopes, setScopes] = useState<string[]>(saved.current?.scopes || []);
  const [pkce, setPkce] = useState(saved.current?.pkce ?? true);
  const [resource, setResource] = useState(saved.current?.resource || '');
  const [scopeErr, setScopeErr] = useState('');
  const [secrets, setSecrets] = useState(() => ({
    state: saved.current?.state || randomToken(12),
    nonce: saved.current?.nonce || randomToken(12),
    verifier: saved.current?.verifier || randomToken(32),
  }));
  const [challenge, setChallenge] = useState('');

  const [exchanging, setExchanging] = useState(false);
  const [tokens, setTokens] = useState<TokenResult | null>(null);
  const [userinfo, setUserinfo] = useState<{ ok: boolean; body: unknown } | null>(null);
  const [callingUserinfo, setCallingUserinfo] = useState(false);
  const headingRef = useRef<HTMLHeadingElement>(null);

  const client = clients.find((c) => c.id === clientRowId);

  // Pick a sensible client + redirect once the list is known.
  const initialised = useRef(false);
  useEffect(() => {
    if (initialised.current || !clients.length) return;
    initialised.current = true;
    const pick = clients.find((c) => c.id === clientRowId) || clients[0];
    pickClient(pick.id, !!saved.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clients]);

  function pickClient(id: string, keepSaved = false) {
    const c = clients.find((x) => x.id === id);
    if (!c) return;
    setClientRowId(id);
    if (!keepSaved || !saved.current) {
      setRedirect(c.redirect_uris.includes(CALLBACK_URI()) ? CALLBACK_URI() : c.redirect_uris[0]);
      const defaults = ['openid', 'profile', 'email'].filter((s) => c.allowed_scopes.includes(s));
      setScopes(defaults.length ? defaults : [...c.allowed_scopes]);
      setPkce(true);
    }
  }

  useEffect(() => {
    if (!pkce) {
      setChallenge('');
      return;
    }
    let live = true;
    void s256(secrets.verifier).then((c) => live && setChallenge(c));
    return () => {
      live = false;
    };
  }, [secrets.verifier, pkce]);

  const invalidate = useCallback(() => {
    clearSession(PG_CALLBACK_KEY);
    clearSession(PG_SESSION_KEY);
    saved.current = null;
    setCallback(null);
    setTokens(null);
    setUserinfo(null);
    setReached(1);
  }, []);

  const hasOpenid = scopes.includes('openid');
  const authParams: [string, string, string][] = client
    ? [
        ['client_id', client.client_id, `Identifies ${client.name}.`],
        ['redirect_uri', redirect, 'Must exactly match a registered URI.'],
        ['response_type', 'code', 'Authorization code flow.'],
        ['scope', scopes.join(' '), 'What the app asks the user for.'],
        ['state', secrets.state, 'Random. Checked on the callback to stop CSRF.'],
        ...(hasOpenid
          ? ([['nonce', secrets.nonce, 'Random. Echoed in the ID token to stop replay.']] as [string, string, string][])
          : []),
        ...(pkce
          ? ([
              ['code_challenge', challenge || '…', 'Base64url SHA-256 of the code_verifier.'],
              ['code_challenge_method', 'S256', 'The only method authrz.me accepts.'],
            ] as [string, string, string][])
          : []),
      ]
    : [];
  const authUrl = `${API_URL}/oauth/authorize?${new URLSearchParams(authParams.map(([k, v]) => [k, v])).toString()}`;

  const go = (n: number) => {
    if (n > reached) return;
    setStep(n);
    window.setTimeout(() => headingRef.current?.focus(), 0);
  };

  const regenerate = () => {
    invalidate();
    setSecrets({ state: randomToken(12), nonce: randomToken(12), verifier: randomToken(32) });
    setStep(1);
  };

  const reset = () => {
    regenerate();
    toast({ title: 'Playground reset', description: 'New state, nonce and verifier.' });
  };

  const continueToSignIn = () => {
    if (!client) return;
    if (!scopes.length) {
      setScopeErr('Choose at least one scope.');
      return;
    }
    if (pkce && !challenge) return;
    setReached((r) => Math.max(r, 2));
    go(2);
  };

  const sendToAuthrz = () => {
    if (!client) return;
    const session: PlaygroundSession = {
      clientRowId: client.id,
      clientId: client.client_id,
      clientName: client.name,
      redirectUri: redirect,
      scopes,
      pkce,
      state: secrets.state,
      nonce: secrets.nonce,
      verifier: secrets.verifier,
      resource: resource.trim(),
    };
    clearSession(PG_CALLBACK_KEY);
    writeSession(PG_SESSION_KEY, session);
    window.location.href = authUrl;
  };

  const stateMatches = !!callback && !!saved.current && callback.state === saved.current.state;

  const exchange = async () => {
    const s = saved.current;
    if (!s || !callback?.code) return;
    setExchanging(true);
    try {
      const res = await fetch(`${API_URL}/oauth/token`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams({
          grant_type: 'authorization_code',
          code: callback.code,
          redirect_uri: s.redirectUri,
          client_id: s.clientId,
          ...(s.pkce ? { code_verifier: s.verifier } : {}),
          ...(s.resource ? { resource: s.resource } : {}),
        }),
      });
      const body = (await res.json().catch(() => ({}))) as Record<string, unknown>;
      setTokens({ ok: res.ok && !body.error, status: res.status, body });
      setReached(4);
      go(4);
      if (res.ok && !body.error) {
        toast({
          title: 'Tokens issued',
          description: body.refresh_token ? 'Access, ID and refresh token.' : body.id_token ? 'Access and ID token.' : 'Access token.',
        });
      }
    } catch (error) {
      setTokens({
        ok: false,
        status: 0,
        body: { error: 'network_error', error_description: error instanceof Error ? error.message : 'Request failed' },
      });
      setReached(4);
      go(4);
    } finally {
      setExchanging(false);
    }
  };

  const callUserinfo = async () => {
    const at = tokens?.body.access_token;
    if (typeof at !== 'string') return;
    setCallingUserinfo(true);
    try {
      const res = await fetch(`${API_URL}/oauth/userinfo`, { headers: { Authorization: `Bearer ${at}` } });
      setUserinfo({ ok: res.ok, body: await res.json().catch(() => ({})) });
    } catch (error) {
      setUserinfo({ ok: false, body: { error: 'network_error', message: String(error) } });
    } finally {
      setCallingUserinfo(false);
    }
  };

  /* ── empty / loading ── */
  if (isLoading) {
    return (
      <div>
        <PageHeader title="OAuth playground" />
        <div className="h-64 animate-pulse rounded-lg border border-border bg-card" aria-busy="true" />
      </div>
    );
  }
  if (!clients.length && !resumed) {
    return (
      <div>
        <PageHeader title="OAuth playground" />
        <div className="rounded-lg border border-border bg-card">
          <EmptyState
            icon={<Play />}
            title="No public client to test with"
            action={
              canCreate ? (
                <Button asChild>
                  <Link to="/clients/new">
                    <Plus className="mr-2 h-4 w-4" />
                    New client
                  </Link>
                </Button>
              ) : undefined
            }
          >
            The playground runs the authorization code + PKCE flow with an active public client that
            has at least one redirect URI.
            {!canCreate && ' Ask a super admin to create one.'}
          </EmptyState>
        </div>
      </div>
    );
  }

  const fullName = user?.name || user?.email || 'You';
  const sessionClientName = saved.current?.clientName || client?.name || 'client';

  return (
    <div>
      <PageHeader
        eyebrow={
          <>
            <Play className="h-3.5 w-3.5" />
            Developer
          </>
        }
        title="OAuth playground"
        description={
          <>
            Walk the authorization code + PKCE flow end to end as <b>{fullName}</b>. The sign-in,
            redirect and token exchange are real requests against {API_URL.replace(/^https?:\/\//, '')}.
          </>
        }
        actions={
          <>
            <Button variant="outline" asChild>
              <Link to="/docs#ep-authorize">
                <BookOpen className="mr-2 h-4 w-4" />
                Reference
              </Link>
            </Button>
            <Button variant="outline" onClick={reset}>
              <RefreshCw className="mr-2 h-4 w-4" />
              Start over
            </Button>
          </>
        }
      />

      <div className="grid gap-6 md:grid-cols-[220px_minmax(0,1fr)] md:items-start md:gap-7">
        <nav aria-label="Flow steps" className="flex gap-1 overflow-x-auto md:sticky md:top-20 md:flex-col">
          {STEPS.map((label, i) => {
            const n = i + 1;
            const on = n === step;
            const done = n < reached && !on;
            return (
              <button
                key={label}
                type="button"
                disabled={n > reached}
                aria-current={on ? 'step' : undefined}
                onClick={() => go(n)}
                className={cn(
                  'flex shrink-0 items-center gap-2.5 rounded-md px-2.5 py-2 text-left text-[13.5px] text-muted-foreground hover:bg-foreground/[0.04] hover:text-foreground disabled:cursor-not-allowed disabled:opacity-45 disabled:hover:bg-transparent disabled:hover:text-muted-foreground',
                  on && 'bg-foreground/[0.06] font-semibold text-foreground'
                )}
              >
                <span
                  className={cn(
                    'flex h-5 w-5 items-center justify-center rounded-full border border-border font-mono text-[11px]',
                    on && 'border-foreground bg-foreground text-background',
                    done && 'border-success text-success'
                  )}
                >
                  {done ? <Check className="h-3 w-3" /> : n}
                </span>
                {label}
              </button>
            );
          })}
        </nav>

        <div className="min-w-0">
          {/* ───────── 1 · configure ───────── */}
          {step === 1 && client && (
            <form
              noValidate
              className="space-y-5"
              onSubmit={(e) => {
                e.preventDefault();
                continueToSignIn();
              }}
            >
              <Card
                title="Configure the request"
                sub="Pick a public client. The playground builds the same request your app would send."
                headingRef={headingRef}
              >
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="space-y-1.5">
                    <DevLabel htmlFor="pClient">Client</DevLabel>
                    <Select
                      value={clientRowId}
                      onValueChange={(v) => {
                        invalidate();
                        pickClient(v);
                      }}
                    >
                      <SelectTrigger id="pClient">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {clients.map((c) => (
                          <SelectItem key={c.id} value={c.id}>
                            {c.name}
                            {c.product_name ? ` · ${c.product_name}` : ''}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Hint>
                      Active public clients with a redirect URI.{' '}
                      {canCreate && (
                        <Link to={`/clients/${client.id}`} className="text-primary hover:underline">
                          Open client
                        </Link>
                      )}
                    </Hint>
                  </div>
                  <div className="space-y-1.5">
                    <DevLabel htmlFor="pRedirect">Redirect URI</DevLabel>
                    <Select
                      value={redirect}
                      onValueChange={(v) => {
                        invalidate();
                        setRedirect(v);
                      }}
                    >
                      <SelectTrigger id="pRedirect" className="font-mono">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {client.redirect_uris.map((u) => (
                          <SelectItem key={u} value={u} className="font-mono">
                            {u}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Hint>Registered on this client.</Hint>
                  </div>
                </div>

                {redirect !== CALLBACK_URI() && (
                  <Callout tone="warn" title="This URI won’t return to the playground">
                    To see the callback and exchange the code here, register{' '}
                    <Mono className="break-all">{CALLBACK_URI()}</Mono> as a redirect URI on this
                    client{canCreate ? ' (Redirect URIs tab)' : ''}.
                  </Callout>
                )}

                <div className="space-y-1.5">
                  <DevLabel>Scopes</DevLabel>
                  <ScopePicker
                    value={scopes}
                    disabledScopes={['openid', 'profile', 'email', 'offline_access', 'products', 'organizations'].filter(
                      (s) => !client.allowed_scopes.includes(s)
                    )}
                    onChange={(v) => {
                      invalidate();
                      setScopes(v);
                      setScopeErr('');
                    }}
                    name="pScope"
                  />
                  <InlineError>{scopeErr}</InlineError>
                </div>

                <div className="flex items-center justify-between gap-3 rounded-lg border border-border px-3.5 py-3">
                  <div>
                    <b className="text-[13.5px] font-semibold">PKCE · S256</b>
                    <p className="text-[12.5px] text-muted-foreground">
                      {client.pkce_required ? 'Required for this client.' : 'Recommended.'}
                    </p>
                  </div>
                  <Switch
                    checked={pkce}
                    disabled={client.pkce_required}
                    onCheckedChange={(v) => {
                      invalidate();
                      setPkce(v);
                    }}
                    aria-label="Use PKCE"
                  />
                </div>

                <div className="space-y-1.5">
                  <DevLabel htmlFor="pResource">Resource audience (optional)</DevLabel>
                  <Input
                    id="pResource"
                    className="font-mono"
                    value={resource}
                    onChange={(e) => setResource(e.target.value)}
                    placeholder="https://api.example.com"
                    spellCheck={false}
                  />
                  <Hint>RFC 8707 resource sent to the token endpoint. It must match the app’s resource.</Hint>
                </div>

                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="space-y-1">
                    <DevLabel>state</DevLabel>
                    <CopyId value={secrets.state} className="w-full" />
                  </div>
                  <div className="space-y-1">
                    <DevLabel>nonce</DevLabel>
                    <CopyId value={secrets.nonce} className="w-full" />
                  </div>
                  <div className="space-y-1 sm:col-span-2">
                    <DevLabel>code_verifier</DevLabel>
                    <CopyId value={secrets.verifier} className="w-full" />
                    <Hint>Kept by the app until the token exchange. Never sent to authorize.</Hint>
                  </div>
                </div>
                <div>
                  <Button type="button" variant="outline" size="sm" onClick={regenerate}>
                    <RefreshCw className="mr-1.5 h-3.5 w-3.5" />
                    Regenerate state, nonce &amp; verifier
                  </Button>
                </div>
              </Card>

              <Card
                title="Authorization request"
                sub="The browser is sent to this URL."
                footer={
                  <Button type="submit" disabled={pkce && !challenge}>
                    Continue
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                }
                actions={<CopyUrl value={authUrl} />}
              >
                <UrlBox base={`${API_URL}/oauth/authorize`} params={authParams} />
                <ParamList rows={authParams} />
              </Card>
            </form>
          )}

          {/* ───────── 2 · sign in & consent ───────── */}
          {step === 2 && client && (
            <section className="overflow-hidden rounded-lg border border-border bg-card">
              <CardHead
                title={
                  <>
                    Sign in &amp; consent <Tag>preview</Tag>
                  </>
                }
                sub={
                  <>
                    What {fullName} sees at <Mono>/oauth/authorize</Mono>. Continuing sends this
                    browser to authrz.me for real.
                  </>
                }
                headingRef={headingRef}
              />
              <div className="grid place-items-center bg-background px-4 py-7">
                <div
                  role="group"
                  aria-label="Consent screen preview"
                  className="flex w-full max-w-[400px] flex-col gap-4 rounded-xl border border-border bg-card p-5 shadow-sm"
                >
                  <div className="flex items-center justify-between gap-3">
                    <span className="flex items-center gap-2 font-mono text-sm font-semibold">
                      <BrandGlyph className="h-5 w-5" />
                      authrz.me
                    </span>
                    <StatusPill>Sign-in request</StatusPill>
                  </div>
                  <h3 className="text-[17px] font-semibold tracking-tight">
                    {client.name} wants to use your authrz.me account
                  </h3>
                  <div className="flex items-center gap-2.5 rounded-lg border border-border px-3 py-2.5">
                    <span className="flex h-8 w-8 items-center justify-center rounded-full bg-foreground/[0.07] font-mono text-xs font-semibold">
                      {(user?.name || user?.email || '?').slice(0, 2).toUpperCase()}
                    </span>
                    <span className="min-w-0 flex-1">
                      <b className="block truncate text-[13px]">{user?.name || user?.email}</b>
                      <span className="block truncate text-xs text-muted-foreground" title={user?.email}>
                        {user?.email}
                      </span>
                    </span>
                    <StatusPill tone="ok">Signed in</StatusPill>
                  </div>
                  <div className="space-y-2">
                    <DevLabel>This will allow {client.product_name || 'the app'} to</DevLabel>
                    <ul className="space-y-2">
                      {scopes.map((s) => (
                        <li key={s} className="grid grid-cols-[auto_minmax(0,1fr)] items-start gap-2 text-[13px]">
                          <Check className="mt-0.5 h-4 w-4 text-success" />
                          <span>
                            <code className="font-mono text-[12.5px] font-semibold">{s}</code>
                            <span className="block text-xs text-muted-foreground">{SCOPE_INFO[s] || ''}</span>
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    You’ll be sent back to <Mono className="break-all">{safeHost(redirect)}</Mono>. You can
                    revoke access from My access at any time.
                  </p>
                  <p className="text-xs text-muted-foreground">
                    The real consent screen may be skipped if you already allowed this client.
                  </p>
                </div>
              </div>
              <div className="flex flex-wrap justify-between gap-2 border-t border-border px-4 py-3">
                <Button variant="outline" onClick={() => go(1)}>
                  <ArrowLeft className="mr-2 h-4 w-4" />
                  Back to configure
                </Button>
                <Button onClick={sendToAuthrz}>
                  Send user to authrz.me
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </div>
            </section>
          )}

          {/* ───────── 3 · callback ───────── */}
          {step === 3 && callback && (
            <section className="overflow-hidden rounded-lg border border-border bg-card">
              {callback.error || !callback.code ? (
                <>
                  <CardHead
                    title="Callback · authorization failed"
                    sub="authrz.me redirected back with an error instead of a code."
                    headingRef={headingRef}
                  />
                  <div className="space-y-4 p-4 sm:p-[18px]">
                    <StatusIcon ok={false} />
                    {(() => {
                      const rows: [string, string][] = [
                        ['error', callback.error || 'missing_code'],
                        ...(callback.errorDescription ? ([['error_description', callback.errorDescription]] as [string, string][]) : []),
                        ...(callback.state ? ([['state', callback.state]] as [string, string][]) : []),
                      ];
                      return (
                        <>
                          <UrlBox base={callback.url} params={rows} />
                          <ParamList
                            rows={rows.map(([k, v]) => [
                              k,
                              v,
                              k === 'error'
                                ? 'Show a friendly message; don’t retry automatically.'
                                : k === 'state'
                                  ? 'Still check it, even on errors.'
                                  : '',
                            ])}
                          />
                        </>
                      );
                    })()}
                  </div>
                  <div className="flex flex-wrap justify-between gap-2 border-t border-border px-4 py-3">
                    <span />
                    <Button onClick={reset}>
                      <RefreshCw className="mr-2 h-4 w-4" />
                      Start over
                    </Button>
                  </div>
                </>
              ) : (
                <>
                  <CardHead
                    title="Callback · code received"
                    sub={
                      <>
                        authrz.me redirected the browser to <Mono className="break-all">{callback.url}</Mono>.
                      </>
                    }
                    headingRef={headingRef}
                  />
                  <div className="space-y-4 p-4 sm:p-[18px]">
                    <StatusIcon ok={stateMatches} />
                    <UrlBox
                      base={callback.url}
                      params={[
                        ['code', callback.code],
                        ['state', callback.state ?? ''],
                      ]}
                    />
                    <ParamList
                      rows={[
                        ['code', callback.code, 'Single use. Exchange it right away.'],
                        [
                          'state',
                          callback.state ?? '(missing)',
                          'Compared with the value you stored before redirecting.',
                          stateMatches ? (
                            <StatusPill tone="ok">Matches the state you sent</StatusPill>
                          ) : (
                            <StatusPill tone="bad">Doesn’t match — discard this response</StatusPill>
                          ),
                        ],
                      ]}
                    />
                    {!stateMatches && (
                      <Callout tone="bad" icon={<AlertTriangle className="h-4 w-4" />} title="Possible CSRF">
                        A state mismatch means this callback wasn’t started by your app. Never
                        exchange the code.
                      </Callout>
                    )}
                  </div>
                  <div className="flex flex-wrap justify-between gap-2 border-t border-border px-4 py-3">
                    <Button variant="outline" onClick={reset}>
                      <ArrowLeft className="mr-2 h-4 w-4" />
                      Start over
                    </Button>
                    <Button disabled={!stateMatches || !saved.current} onClick={() => { setReached(4); go(4); }}>
                      Exchange code
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                  </div>
                </>
              )}
            </section>
          )}

          {/* ───────── 4 · tokens ───────── */}
          {step === 4 && saved.current && callback?.code && (
            <TokensStep
              session={saved.current}
              code={callback.code}
              tokens={tokens}
              exchanging={exchanging}
              onExchange={() => void exchange()}
              onBack={() => go(3)}
              onReset={reset}
              userinfo={userinfo}
              callingUserinfo={callingUserinfo}
              onUserinfo={() => void callUserinfo()}
              headingRef={headingRef}
              clientName={sessionClientName}
            />
          )}
        </div>
      </div>
    </div>
  );
}

/* ── token step ─────────────────────────────────────────── */

function TokensStep({
  session,
  code,
  tokens,
  exchanging,
  onExchange,
  onBack,
  onReset,
  userinfo,
  callingUserinfo,
  onUserinfo,
  headingRef,
  clientName,
}: {
  session: PlaygroundSession;
  code: string;
  tokens: TokenResult | null;
  exchanging: boolean;
  onExchange: () => void;
  onBack: () => void;
  onReset: () => void;
  userinfo: { ok: boolean; body: unknown } | null;
  callingUserinfo: boolean;
  onUserinfo: () => void;
  headingRef: React.RefObject<HTMLHeadingElement>;
  clientName: string;
}) {
  const request = `curl -X POST ${API_URL}/oauth/token \\
  -d grant_type=authorization_code \\
  -d code=${code} \\
  -d redirect_uri=${session.redirectUri} \\
  -d client_id=${session.clientId}${session.pkce ? ` \\
  -d code_verifier=${session.verifier}` : ''}${session.resource ? ` \\
  -d resource=${session.resource}` : ''}`;

  const body = tokens?.body ?? {};
  const shownResp = Object.fromEntries(
    Object.entries(body).map(([k, v]) => [k, /token$/.test(k) && typeof v === 'string' ? shorten(v) : v])
  );
  const idJwt = decodeJwt(typeof body.id_token === 'string' ? body.id_token : null);
  const accessJwt = decodeJwt(typeof body.access_token === 'string' ? body.access_token : null);
  const kid = (idJwt.header?.kid ?? accessJwt.header?.kid) as string | undefined;
  const fmtTime = (v: unknown) =>
    typeof v === 'number'
      ? new Date(v * 1000).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
      : '';

  const check = (k: string, v: unknown): React.ReactNode => {
    switch (k) {
      case 'iss':
        return v === API_URL || (typeof v === 'string' && v.replace(/\/$/, '') === API_URL) ? (
          <StatusPill tone="ok">matches issuer</StatusPill>
        ) : (
          <StatusPill tone="bad">unexpected issuer</StatusPill>
        );
      case 'aud':
        return v === session.clientId || (Array.isArray(v) && v.includes(session.clientId)) ? (
          <StatusPill tone="ok">your client_id</StatusPill>
        ) : (
          <StatusPill tone="bad">wrong audience</StatusPill>
        );
      case 'nonce':
        return v === session.nonce ? (
          <StatusPill tone="ok">matches nonce</StatusPill>
        ) : (
          <StatusPill tone="bad">nonce mismatch</StatusPill>
        );
      case 'exp':
        return typeof v === 'number' && v * 1000 > Date.now() ? (
          <StatusPill tone="ok">not expired</StatusPill>
        ) : (
          <StatusPill tone="bad">expired</StatusPill>
        );
      default:
        return null;
    }
  };

  return (
    <div className="space-y-5">
      <section className="overflow-hidden rounded-lg border border-border bg-card">
        <CardHead
          title="Exchange the code"
          sub={`${clientName} posts the code ${session.pkce ? 'and the original code_verifier ' : ''}to the token endpoint. No secret — this is a public client.`}
          headingRef={headingRef}
        />
        <div className="p-4 sm:p-[18px]">
          <CodeBlock label="POST /oauth/token" code={request} />
        </div>
        <div className="flex flex-wrap justify-between gap-2 border-t border-border px-4 py-3">
          <Button variant="outline" onClick={onBack}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back
          </Button>
          {tokens ? (
            <Button variant="outline" onClick={onReset}>
              <RefreshCw className="mr-2 h-4 w-4" />
              Start over
            </Button>
          ) : (
            <Button onClick={onExchange} disabled={exchanging}>
              {exchanging ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Send className="mr-2 h-4 w-4" />}
              {exchanging ? 'Requesting…' : 'Send token request'}
            </Button>
          )}
        </div>
      </section>

      {tokens && !tokens.ok && (
        <Callout tone="bad" icon={<XCircle className="h-4 w-4" />} title={`Token request failed${tokens.status ? ` · ${tokens.status}` : ''}`}>
          <p>
            <Mono>{String(body.error ?? 'error')}</Mono>
            {body.error_description ? ` — ${String(body.error_description)}` : ''}. Authorization codes
            are single use; start over to get a fresh one.
          </p>
        </Callout>
      )}

      {tokens && (
        <Card
          title={
            <>
              Token response{' '}
              <StatusPill tone={tokens.ok ? 'ok' : 'bad'}>{tokens.ok ? '200 OK' : `${tokens.status || 'Error'}`}</StatusPill>
            </>
          }
          sub={tokens.ok ? 'Tokens are shortened here; copy gets the full JSON.' : undefined}
        >
          <CodeBlock label="application/json" code={JSON.stringify(body, null, 2)} shown={JSON.stringify(shownResp, null, 2)} />
        </Card>
      )}

      {tokens?.ok && idJwt.payload && idJwt.parts && (
        <Card
          title="ID token"
          sub={
            <>
              {String(idJwt.header?.alg ?? 'RS256')}, signed with <Mono>{kid ?? 'kid'}</Mono>. Verify against the JWKS before
              trusting claims.
            </>
          }
        >
          <div
            aria-label="Encoded ID token"
            className="break-all rounded-lg border border-border bg-foreground/[0.03] p-3 font-mono text-[12.5px] leading-relaxed"
          >
            <span className="text-muted-foreground">{idJwt.parts[0]}</span>
            <span className="text-muted-foreground">.</span>
            <span className="font-semibold">{idJwt.parts[1]}</span>
            <span className="text-muted-foreground">.</span>
            <span className="italic text-muted-foreground">{shorten(idJwt.parts[2], 40)}</span>
          </div>
          <div className="flex flex-wrap gap-x-4 gap-y-1.5 text-[12.5px] text-muted-foreground">
            <span><Tag>header</Tag> algorithm &amp; key</span>
            <span><Tag>payload</Tag> claims</span>
            <span><Tag>signature</Tag> RS256</span>
          </div>
          <div className="overflow-hidden rounded-lg border border-border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Claim</TableHead>
                  <TableHead>Value</TableHead>
                  <TableHead>Check</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {Object.entries(idJwt.payload).map(([k, v]) => (
                  <TableRow key={k}>
                    <TableCell>
                      <Mono className="font-semibold">{k}</Mono>
                    </TableCell>
                    <TableCell className="whitespace-normal break-all">
                      <Mono>{typeof v === 'object' ? JSON.stringify(v) : String(v)}</Mono>
                      {['exp', 'iat', 'auth_time'].includes(k) && (
                        <span className="block text-xs text-muted-foreground">{fmtTime(v)}</span>
                      )}
                    </TableCell>
                    <TableCell>{check(k, v)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </Card>
      )}

      {tokens?.ok && accessJwt.payload && (
        <Card title="Access token payload" sub={<>Send it as <Mono>Authorization: Bearer …</Mono> to your API.</>}>
          <CodeBlock label="JWT payload" code={JSON.stringify(accessJwt.payload, null, 2)} />
        </Card>
      )}

      {tokens?.ok && (
        <Card
          title="UserInfo"
          sub={<><Mono>GET /oauth/userinfo</Mono> with the access token.</>}
          actions={
            <Button variant="outline" size="sm" onClick={onUserinfo} disabled={callingUserinfo}>
              {callingUserinfo ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <Send className="mr-1.5 h-3.5 w-3.5" />}
              {userinfo ? 'Call again' : 'Call userinfo'}
            </Button>
          }
        >
          {userinfo ? (
            <CodeBlock
              label={userinfo.ok ? 'Response · 200' : 'Response · error'}
              code={JSON.stringify(userinfo.body, null, 2)}
            />
          ) : (
            <p className="text-[13px] text-muted-foreground">Not called yet.</p>
          )}
        </Card>
      )}
    </div>
  );
}

/* ── small local pieces ─────────────────────────────────── */

function safeHost(u: string): string {
  try {
    return new URL(u).host || u;
  } catch {
    return u;
  }
}

function CardHead({
  title,
  sub,
  actions,
  headingRef,
}: {
  title: React.ReactNode;
  sub?: React.ReactNode;
  actions?: React.ReactNode;
  headingRef?: React.RefObject<HTMLHeadingElement>;
}) {
  return (
    <div className="flex items-start justify-between gap-3 border-b border-border px-4 py-3.5 sm:px-[18px]">
      <div className="min-w-0">
        <h2 ref={headingRef} tabIndex={-1} className="flex flex-wrap items-center gap-2 text-[15px] font-semibold tracking-tight outline-none">
          {title}
        </h2>
        {sub && <p className="mt-0.5 text-[13px] text-muted-foreground">{sub}</p>}
      </div>
      {actions}
    </div>
  );
}

function Card({
  title,
  sub,
  actions,
  footer,
  headingRef,
  children,
}: {
  title: React.ReactNode;
  sub?: React.ReactNode;
  actions?: React.ReactNode;
  footer?: React.ReactNode;
  headingRef?: React.RefObject<HTMLHeadingElement>;
  children: React.ReactNode;
}) {
  return (
    <section className="min-w-0 overflow-hidden rounded-lg border border-border bg-card">
      <CardHead title={title} sub={sub} actions={actions} headingRef={headingRef} />
      <div className="space-y-4 p-4 sm:p-[18px]">{children}</div>
      {footer && <div className="flex justify-end gap-2 border-t border-border px-4 py-3 sm:px-[18px]">{footer}</div>}
    </section>
  );
}

function CopyUrl({ value }: { value: string }) {
  const [done, setDone] = useState(false);
  return (
    <Button
      type="button"
      variant="ghost"
      size="icon"
      aria-label="Copy authorization URL"
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(value);
          setDone(true);
          window.setTimeout(() => setDone(false), 1500);
        } catch {
          /* clipboard unavailable */
        }
      }}
    >
      {done ? <Check className="h-4 w-4 text-success" /> : <Copy className="h-4 w-4" />}
    </Button>
  );
}

function UrlBox({ base, params }: { base: string; params: (readonly [string, string, ...unknown[]])[] | [string, string][] }) {
  return (
    <div className="break-all rounded-lg border border-border bg-foreground/[0.03] p-3 font-mono text-[12.5px] leading-[1.7]">
      {base}
      {params.map(([k, v], i) => (
        <span key={k}>
          <span className="text-muted-foreground">{i ? '&' : '?'}</span>
          {k}={encodeURIComponent(v)}
        </span>
      ))}
    </div>
  );
}

function ParamList({ rows }: { rows: [string, string, string?, React.ReactNode?][] }) {
  return (
    <div className="flex flex-col">
      {rows.map(([k, v, d, extra]) => (
        <div
          key={k}
          className="grid gap-x-3.5 gap-y-1 border-t border-border py-2.5 first:border-t-0 first:pt-0 sm:grid-cols-[170px_minmax(0,1fr)]"
        >
          <code className="font-mono text-[12.5px] font-semibold">{k}</code>
          <span className="grid min-w-0 gap-0.5">
            <code className="break-all font-mono text-[12.5px]">{v}</code>
            {d && <span className="text-[12.5px] text-muted-foreground">{d}</span>}
            {extra && <span className="mt-1">{extra}</span>}
          </span>
        </div>
      ))}
    </div>
  );
}

function StatusIcon({ ok }: { ok: boolean }) {
  return ok ? (
    <CheckCircle2 className="h-7 w-7 text-success" aria-label="Success" />
  ) : (
    <XCircle className="h-7 w-7 text-destructive" aria-label="Failed" />
  );
}
